import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from './ui/tooltip';
import {
  Plus, Search, Filter, X, BarChart2, Eye, Clock,
  AlertTriangle, Check, Trash2, FileText, ChevronDown, ChevronUp, ChevronRight,
  Loader2, Bell, QrCode, Scissors, ArrowRight, Zap,
  ArrowLeft, Package, PieChart, Layers, AlertCircle
} from 'lucide-react';
import LoteCardExpandido from './gestor/lotes/LoteCardExpandido';
import { CategoriaCard } from './gestor/lotes';
import CriarLoteForm from './CriarLoteForm';

/**
 * GestaoLotesHeader
 * Cabeçalho + Dashboard + Filtros + CriarLoteForm
 */
export default function GestaoLotesHeader({
  // OF data
  ofId,
  resumo = {},
  quantidadeTotal = 0,
  categoriasFiltradas = [],
  operacoesPorCategoria = {},
  lotes = [],
  operadores = [],
  onClose = null,
  numeroOf = '',
  marca = '',
  dataEntrega = null,
  // State
  filtroCategoria,
  setFiltroCategoria,
  pesquisa,
  setPesquisa,
  saving = false,
  setSaving,
  showNovoLote,
  setShowNovoLote,
  loadingAnalise = false,
  showAnalise = false,
  setShowAnalise,
  aprovando = null,
  // Callbacks
  onUpdate,
  loadData,
  handleAprovarLote,
  handleEliminarLote,
  handleGerarPDF,
  openAvancarLote,
  openDividirLote,
  openEditarQuantidade,
  openEditObservacoes,
  openHistorico,
  openRetrocederLote,
  handleAlertarOperadores,
  onEditOperacoes,
  createLoteContext,
  unirLotesContext,
  getCategoriaCor,
  onOpenCriarLoteArranjo,
  onOpenTerceirizar,
}) {
  const [categoriaExpandida, setCategoriaExpandida] = React.useState('');
  const todosLotesFiltrados = lotes.filter(l => {
    const catOk = !filtroCategoria || filtroCategoria === 'todas' || l.categoria_id === filtroCategoria;
    const pesquisaOk = !pesquisa || (l.codigo_lote || '').toLowerCase().includes(pesquisa.toLowerCase());
    return catOk && pesquisaOk;
  });

  return (
    <>
{/* Cabeçalho com resumo */}
      <div className="bg-gradient-to-r from-navy via-navy/90 to-navy/80 text-white p-6 rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {onClose && (
              <Button 
                variant="outline" 
                onClick={onClose}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30 gap-2 px-4"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="font-medium">Voltar</span>
              </Button>
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <Package className="h-7 w-7" />
                Gestão de Lotes
              </h2>
              <div className="flex items-center gap-4 text-white/70 mt-1">
                <span>OF: {numeroOf}</span>
                {marca && <span>| Marca: <span className="text-white font-medium">{marca}</span></span>}
                {dataEntrega && <span>| Entrega: <span className="text-white font-medium">{new Date(dataEntrega).toLocaleDateString('pt-PT')}</span></span>}
              </div>
            </div>
            
            {/* Botão Análise de Tempos */}
            <Button
              onClick={() => setShowAnalise(true)}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 gap-2"
              variant="outline"
            >
              <PieChart className="h-4 w-4" />
              Análise de Tempos
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-3xl font-bold">{resumo.quantidade_total || 0}</p>
            <p className="text-xs text-white/70 uppercase">Total OF</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-3xl font-bold text-red-300">{resumo.total_anomalias || 0}</p>
            <p className="text-xs text-white/70 uppercase">Anomalias</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-3xl font-bold text-amber-300">{resumo.quantidade_pendente || 0}</p>
            <p className="text-xs text-white/70 uppercase">Pendente</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-3xl font-bold text-blue-300">{resumo.quantidade_restante || 0}</p>
            <p className="text-xs text-white/70 uppercase">Por Alocar</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-3xl font-bold">{lotes.length}</p>
            <p className="text-xs text-white/70 uppercase">Total Lotes</p>
          </div>
        </div>
      </div>

      {/* Dashboard de Distribuição por Categoria */}
      {categoriasFiltradas.length > 0 && (
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-stone-700 mb-3 flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Distribuição por Categoria
            <span className="text-xs font-normal text-stone-500 ml-auto">
              Limite OF: {resumo.quantidade_total || quantidadeTotal} peças
            </span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {categoriasFiltradas.filter(cat => cat.codigo !== 'ARR' && !cat.especial).map(cat => (
              <CategoriaCard
                key={cat.id}
                categoria={cat}
                lotes={lotes}
                isExpandida={categoriaExpandida === cat.id}
                onToggleExpand={setCategoriaExpandida}
                limiteOF={resumo.quantidade_total || quantidadeTotal}
                getCategoriaCor={getCategoriaCor}
                ofId={ofId}
                aprovando={aprovando}
                onAprovarLote={handleAprovarLote}
                onEliminarLote={handleEliminarLote}
                onAvancarLote={openAvancarLote}
                onDividirLote={openDividirLote}
                onGerarPDF={handleGerarPDF}
                onEditarQuantidade={openEditarQuantidade}
                onTerceirizar={onOpenTerceirizar}
              />
            ))}
            
            {/* Peças em Arranjo (Card Especial — expansível + criar) */}
            {(() => {
              const lotesArr = (lotes || []).filter(l => l.categoria_codigo === 'ARR' || l.origem === 'arranjo');
              const isExp = categoriaExpandida === '__ARR__';
              const pecasDisp = resumo.pecas_arranjo_disponivel || 0;
              return (
                <div className={`rounded-xl border-2 ${pecasDisp > 0 ? 'border-red-400 bg-gradient-to-br from-red-50 via-rose-50 to-red-100' : (lotesArr.length > 0 ? 'border-rose-200 bg-rose-50/60' : 'border-stone-200 bg-stone-50 opacity-75')}`} data-testid="card-em-arranjo-expansivel">
                  {/* Cabeçalho clicável (toggle) */}
                  <button
                    type="button"
                    onClick={() => setCategoriaExpandida(isExp ? null : '__ARR__')}
                    className="w-full p-3 flex items-center justify-between text-left hover:bg-rose-100/40 rounded-t-xl transition"
                    data-testid="card-em-arranjo"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded ${pecasDisp > 0 ? 'bg-red-600 text-white' : 'bg-stone-300 text-stone-600'}`}>ARR</span>
                      <span className={`text-sm font-medium ${pecasDisp > 0 ? 'text-red-700' : 'text-stone-600'}`}>Em Arranjo</span>
                      {lotesArr.length > 0 && (
                        <span className="text-[11px] text-rose-700 font-semibold bg-white/70 px-1.5 py-0.5 rounded">
                          {lotesArr.length} {lotesArr.length === 1 ? 'lote' : 'lotes'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className={`text-2xl font-bold leading-none ${pecasDisp > 0 ? 'text-red-600' : 'text-stone-500'}`} data-testid="pecas-arranjo-disponivel">{pecasDisp}</p>
                        <p className="text-[10px] text-stone-500">disp.</p>
                      </div>
                      {isExp ? <ChevronDown className="h-4 w-4 text-stone-500" /> : <ChevronRight className="h-4 w-4 text-stone-500" />}
                    </div>
                  </button>
                  {/* Info adicional */}
                  <div className="px-3 pb-2 flex items-center justify-between text-[11px]">
                    <span className={pecasDisp > 0 ? 'text-red-600 font-medium' : 'text-stone-500'}>
                      {pecasDisp > 0 ? 'peças para novo lote ARR' : (lotesArr.length > 0 ? `${resumo.pecas_em_lote_arranjo || 0} já em lote(s)` : 'sem peças disponíveis')}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenCriarLoteArranjo && onOpenCriarLoteArranjo(); }}
                      disabled={!onOpenCriarLoteArranjo || pecasDisp === 0}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold transition ${pecasDisp > 0 ? 'bg-red-600 text-white hover:bg-red-700 shadow-sm' : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`}
                      data-testid="criar-lote-arr-btn"
                    >
                      <Plus className="h-3 w-3" strokeWidth={3} />
                      CRIAR LOTE ARR
                    </button>
                  </div>
                  {/* Lotes expandidos */}
                  {isExp && lotesArr.length > 0 && (
                    <div className="px-3 pb-3 space-y-2 border-t border-rose-200 pt-2" data-testid="lotes-arr-expandidos">
                      {lotesArr.map(lote => (
                        <div key={lote.id} onClick={(e) => e.stopPropagation()}>
                          <LoteCardExpandido
                            lote={lote}
                            ofId={ofId}
                            aprovando={aprovando}
                            onAprovar={handleAprovarLote}
                            onEliminar={handleEliminarLote}
                            onAvancar={openAvancarLote}
                            onDividir={openDividirLote}
                            onGerarPDF={handleGerarPDF}
                            onEditarQuantidade={openEditarQuantidade}
                            onTerceirizar={onOpenTerceirizar}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {isExp && lotesArr.length === 0 && (
                    <div className="px-3 pb-3 text-[11px] text-stone-500 italic border-t border-rose-200 pt-2">
                      Ainda não existem lotes de arranjo nesta OF.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Pendente Classificação */}
            {(resumo.pecas_pendente_classificacao || 0) > 0 && (
              <div className="p-4 rounded-xl border-2 bg-yellow-50 border-yellow-200" data-testid="card-pendente-classificacao">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold bg-white/70 px-2 py-0.5 rounded text-yellow-700">PEND</span>
                    <span className="text-sm font-medium text-yellow-700">Pendente</span>
                  </div>
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                </div>
                <p className="text-3xl font-bold text-yellow-700">{resumo.pecas_pendente_classificacao || 0}</p>
                <p className="text-xs text-yellow-600 mt-1">peças por classificar</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Acções */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => setShowNovoLote(true)}
          className="bg-navy hover:bg-navy/90"
          data-testid="btn-novo-lote"
        >
          <Plus className="h-4 w-4 mr-2" />
          Criar Novo Lote
        </Button>
      </div>

      {/* Barra de Filtros e Pesquisa */}
      {lotes.length > 0 && (
        <div className="flex flex-wrap gap-3 items-center bg-white rounded-lg p-3 border">
          {/* Pesquisa */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              type="text"
              placeholder="Pesquisar lote por código..."
              value={pesquisa}
              onChange={(e) => setPesquisa(e.target.value)}
              className="pl-10 h-10"
              data-testid="input-pesquisa-lote"
            />
          </div>
          
          {/* Filtro por Categoria */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-stone-500" />
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="w-[180px] h-10" data-testid="filtro-categoria">
                <SelectValue placeholder="Filtrar categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as categorias</SelectItem>
                {categoriasFiltradas.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-stone-100 px-1 rounded">{cat.codigo || '---'}</span>
                      {cat.nome}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Limpar filtros */}
          {(pesquisa || filtroCategoria !== 'todas') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPesquisa('');
                setFiltroCategoria('todas');
              }}
              className="text-stone-500"
            >
              <X className="h-4 w-4 mr-1" />
              Limpar
            </Button>
          )}
          
          {/* Contador de resultados */}
          <span className="text-sm text-stone-500 ml-auto">
            {todosLotesFiltrados.length} lotes encontrados
          </span>
        </div>
      )}

      {/* Formulário Criar Novo Lote */}
      {showNovoLote && (
        <CriarLoteForm
          categoriasFiltradas={categoriasFiltradas}
          operacoesPorCategoria={operacoesPorCategoria}
          lotes={lotes}
          resumo={resumo}
          quantidadeTotal={quantidadeTotal}
          saving={saving}
          setSaving={setSaving}
          onClose={() => setShowNovoLote(false)}
          onSuccess={() => { setShowNovoLote(false); loadData(); if (onUpdate) onUpdate(); }}
          createLoteContext={createLoteContext}
          unirLotesContext={unirLotesContext}
          ofId={ofId}
        />
      )}
    </>
  );
}
